import React, { useState, useEffect, useCallback, useMemo } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const LANGUAGES = [
  { code: 'de', name: 'German', nameAr: 'الالمانية', flag: 'DE' },
  { code: 'en', name: 'English', nameAr: 'الانجليزية', flag: 'EN' },
  { code: 'fr', name: 'French', nameAr: 'الفرنسية', flag: 'FR' },
  { code: 'es', name: 'Spanish', nameAr: 'الاسبانية', flag: 'ES' },
  { code: 'it', name: 'Italian', nameAr: 'الايطالية', flag: 'IT' },
  { code: 'pt', name: 'Portuguese', nameAr: 'البرتغالية', flag: 'PT' }
];

const CHANNELS = [
  { code: 'email', name: 'Email', nameAr: 'البريد' },
  { code: 'whatsapp', name: 'WhatsApp', nameAr: 'واتساب' }
];

const SEND_DAY_OPTIONS = [
  { value: 0, labelAr: 'فوري', labelEn: 'Immediately' },
  { value: 1, labelAr: 'بعد يوم', labelEn: 'After 1 day' },
  { value: 2, labelAr: 'بعد يومين', labelEn: 'After 2 days' },
  { value: 3, labelAr: 'بعد 3 ايام', labelEn: 'After 3 days' },
  { value: 5, labelAr: 'بعد 5 ايام', labelEn: 'After 5 days' },
  { value: 7, labelAr: 'بعد اسبوع', labelEn: 'After 1 week' },
  { value: 14, labelAr: 'بعد اسبوعين', labelEn: 'After 2 weeks' }
];

export default function MultiLangAdTemplates({ token }) {
  const isRTL = document.documentElement.dir === 'rtl' || document.documentElement.lang === 'ar';
  
  const [templates, setTemplates] = useState([]);
  const [welcomeTemplates, setWelcomeTemplates] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('de');
  const [selectedChannel, setSelectedChannel] = useState('email');
  const [loading, setLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingWelcome, setEditingWelcome] = useState(null);
  const [notification, setNotification] = useState(null);
  const [activeSection, setActiveSection] = useState('ads');
  
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }), [token]);
  
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };
  
  const loadTemplates = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ad-templates?language=${selectedLanguage}&channel=${selectedChannel}`,
        { headers }
      );
      if (!response.ok) {
        setTemplates([]);
        return;
      }
      const data = await response.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [selectedLanguage, selectedChannel, headers, token]);
  
  const loadWelcomeTemplates = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(
        `${API_URL}/api/ad-templates?language=${selectedLanguage}&channel=${selectedChannel}&type=welcome`,
        { headers }
      );
      if (response.ok) {
        const data = await response.json();
        setWelcomeTemplates(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading welcome templates:', error);
    }
  }, [selectedLanguage, selectedChannel, headers, token]);
  
  useEffect(() => {
    setEditingTemplate(null);
    setEditingWelcome(null);
    loadTemplates();
    loadWelcomeTemplates();
  }, [selectedLanguage, selectedChannel, loadTemplates, loadWelcomeTemplates]);
  
  const saveTemplate = async (adNumber, subject, body, sendDay, isActive = true) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ad-templates`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          language: selectedLanguage,
          channel: selectedChannel,
          adNumber: parseInt(adNumber),
          subject: subject || '',
          body: body || '',
          sendDay: parseInt(sendDay) || 0,
          isActive
        })
      });
      
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        showNotification(isRTL ? 'تعذر الاتصال بالخادم' : 'Server not reachable', 'error');
        return;
      }
      
      const data = await response.json();
      
      if (response.ok) {
        showNotification(isRTL ? 'تم الحفظ' : 'Saved');
        setEditingTemplate(null);
        loadTemplates();
      } else {
        showNotification(data.error || data.message || 'Error', 'error');
      }
    } catch (error) {
      showNotification(isRTL ? 'خطأ في الاتصال' : 'Connection error', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const saveWelcomeTemplate = async (subject, body, isActive = true) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ad-templates/welcome`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          language: selectedLanguage,
          channel: selectedChannel,
          subject: subject || '',
          body: body || '',
          isActive
        })
      });
      
      if (response.ok) {
        showNotification(isRTL ? 'تم الحفظ' : 'Saved');
        setEditingWelcome(null);
        loadWelcomeTemplates();
      } else {
        const data = await response.json();
        showNotification(data.error || 'Error', 'error');
      }
    } catch (error) {
      showNotification(isRTL ? 'خطأ في الاتصال' : 'Connection error', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleActive = async (template) => {
    try {
      await fetch(`${API_URL}/api/ad-templates/${template._id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ isActive: !template.isActive })
      });
      loadTemplates();
      loadWelcomeTemplates();
    } catch (error) {
      showNotification('Error', 'error');
    }
  };
  
  const deleteTemplate = async (id) => {
    if (!window.confirm(isRTL ? 'حذف هذا القالب؟' : 'Delete this template?')) return;
    
    try {
      await fetch(`${API_URL}/api/ad-templates/${id}`, {
        method: 'DELETE',
        headers
      });
      showNotification(isRTL ? 'تم الحذف' : 'Deleted');
      loadTemplates();
    } catch (error) {
      showNotification('Error', 'error');
    }
  };
  
  const getTemplateForAd = (adNum) => templates.find(t => t.adNumber === adNum);
  const getWelcomeTemplate = () => welcomeTemplates.find(t => t.type === 'welcome') || welcomeTemplates[0];
  
  const getSendDayLabel = (day) => {
    const option = SEND_DAY_OPTIONS.find(o => o.value === day);
    if (option) return isRTL ? option.labelAr : option.labelEn;
    return isRTL ? `بعد ${day} يوم` : `After ${day} days`;
  };
  
  return (
    <div className={`space-y-4 ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 ${isRTL ? 'left-4' : 'right-4'} z-50 px-4 py-2 rounded-lg shadow-lg ${
          notification.type === 'error' ? 'bg-red-500' : 'bg-emerald-500'
        } text-white text-sm`}>
          {notification.message}
        </div>
      )}
      
      {/* Header + Variables */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg sm:text-xl font-bold text-white">
          {isRTL ? 'قوالب متعددة اللغات' : 'Multi-Language Templates'}
        </h2>
        <div className="flex flex-wrap gap-1">
          <code className="px-2 py-0.5 bg-slate-700 rounded text-xs text-blue-300" title={isRTL ? 'الاسم الكامل' : 'Full name'}>{'{name}'}</code>
          <code className="px-2 py-0.5 bg-slate-700 rounded text-xs text-emerald-300" title={isRTL ? 'الاسم الاول' : 'First name'}>{'{firstname}'}</code>
          <code className="px-2 py-0.5 bg-slate-700 rounded text-xs text-emerald-300" title={isRTL ? 'الاسم الاخير' : 'Last name'}>{'{lastname}'}</code>
          <code className="px-2 py-0.5 bg-slate-700 rounded text-xs text-pink-300" title={isRTL ? 'ترحيب حسب الجنس' : 'Gender greeting'}>{'{greeting}'}</code>
          <code className="px-2 py-0.5 bg-slate-700 rounded text-xs text-blue-300">{'{email}'}</code>
          <code className="px-2 py-0.5 bg-slate-700 rounded text-xs text-blue-300">{'{phone}'}</code>
        </div>
      </div>
      
      {/* Channel Selector */}
      <div className="grid grid-cols-2 gap-2">
        {CHANNELS.map(ch => (
          <button
            key={ch.code}
            onClick={() => setSelectedChannel(ch.code)}
            className={`px-3 py-2 rounded-lg font-medium transition-all text-sm ${
              selectedChannel === ch.code
                ? ch.code === 'whatsapp' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600'
            }`}
          >
            {isRTL ? ch.nameAr : ch.name}
          </button>
        ))}
      </div>
      
      {/* Language Selector */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
        {LANGUAGES.map(lang => (
          <button
            key={lang.code}
            onClick={() => setSelectedLanguage(lang.code)}
            className={`px-2 py-1.5 rounded-lg font-medium transition-all text-xs ${
              selectedLanguage === lang.code
                ? 'bg-indigo-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600'
            }`}
          >
            {lang.flag}
          </button>
        ))}
      </div>
      
      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        <button
          onClick={() => setActiveSection('welcome')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
            activeSection === 'welcome' ? 'bg-purple-500 text-white' : 'bg-slate-700/50 text-slate-300'
          }`}
        >
          {isRTL ? 'رسالة الترحيب' : 'Welcome'}
        </button>
        <button
          onClick={() => setActiveSection('ads')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
            activeSection === 'ads' ? 'bg-emerald-500 text-white' : 'bg-slate-700/50 text-slate-300'
          }`}
        >
          {isRTL ? 'الاعلانات' : 'Ads'}
        </button>
      </div>
      
      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-4">
          <div className="w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      
      {/* Welcome Section */}
      {activeSection === 'welcome' && !loading && (
        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center bg-purple-500/20 text-purple-400 rounded-full text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
              </div>
              <span className="text-white font-medium text-sm">{isRTL ? 'رسالة الترحيب' : 'Welcome Message'}</span>
            </div>
            
            {getWelcomeTemplate() && (
              <button
                onClick={() => toggleActive(getWelcomeTemplate())}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  getWelcomeTemplate()?.isActive ? 'bg-emerald-500' : 'bg-slate-600'
                }`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  getWelcomeTemplate()?.isActive ? 'translate-x-5' : 'translate-x-0.5'
                }`}></div>
              </button>
            )}
          </div>
          
          {editingWelcome ? (
            <div className="space-y-2">
              {selectedChannel === 'email' && (
                <input
                  type="text"
                  value={editingWelcome.subject}
                  onChange={(e) => setEditingWelcome(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder={isRTL ? 'الموضوع...' : 'Subject...'}
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                />
              )}
              <textarea
                value={editingWelcome.body}
                onChange={(e) => setEditingWelcome(prev => ({ ...prev, body: e.target.value }))}
                placeholder={isRTL ? 'محتوى الرسالة...' : 'Message...'}
                rows={3}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm resize-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => saveWelcomeTemplate(editingWelcome.subject, editingWelcome.body)}
                  className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-medium"
                >
                  {isRTL ? 'حفظ' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingWelcome(null)}
                  className="px-3 py-1.5 bg-slate-600 text-white rounded-lg text-xs"
                >
                  {isRTL ? 'الغاء' : 'Cancel'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              {getWelcomeTemplate() ? (
                <div className="space-y-1">
                  {selectedChannel === 'email' && getWelcomeTemplate().subject && (
                    <p className="text-xs text-slate-400 truncate">{getWelcomeTemplate().subject}</p>
                  )}
                  <p className="text-xs text-slate-300 line-clamp-2">{getWelcomeTemplate().body}</p>
                </div>
              ) : (
                <p className="text-xs text-slate-500">{isRTL ? 'لم يتم الاعداد' : 'Not configured'}</p>
              )}
              <button
                onClick={() => setEditingWelcome({
                  subject: getWelcomeTemplate()?.subject || '',
                  body: getWelcomeTemplate()?.body || ''
                })}
                className="mt-2 px-3 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-xs"
              >
                {isRTL ? 'تعديل' : 'Edit'}
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Ads Section */}
      {activeSection === 'ads' && !loading && (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(adNum => {
            const template = getTemplateForAd(adNum);
            const isEditing = editingTemplate?.adNumber === adNum;
            
            return (
              <div 
                key={adNum} 
                className={`bg-slate-800/50 rounded-lg p-3 border ${
                  template?.isActive ? 'border-emerald-500/30' : 'border-slate-700'
                }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-blue-500/20 text-blue-400 rounded-full font-bold text-xs">
                      {adNum}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      template?.sendDay === 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-600 text-slate-300'
                    }`}>
                      {template ? getSendDayLabel(template.sendDay) : (adNum === 1 ? (isRTL ? 'فوري' : 'Now') : `${adNum}d`)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    {template && (
                      <button
                        onClick={() => toggleActive(template)}
                        className={`relative w-9 h-5 rounded-full transition-colors ${
                          template.isActive ? 'bg-emerald-500' : 'bg-slate-600'
                        }`}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          template.isActive ? 'translate-x-4' : 'translate-x-0.5'
                        }`}></div>
                      </button>
                    )}
                    
                    <button
                      onClick={() => setEditingTemplate(isEditing ? null : { 
                        adNumber: adNum, 
                        subject: template?.subject || '', 
                        body: template?.body || '',
                        sendDay: template?.sendDay ?? (adNum === 1 ? 0 : adNum)
                      })}
                      className={`p-1 rounded text-xs ${isEditing ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {isEditing ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        )}
                      </svg>
                    </button>
                    
                    {template && (
                      <button
                        onClick={() => deleteTemplate(template._id)}
                        className="p-1 rounded bg-red-500/20 text-red-400"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Edit Form */}
                {isEditing ? (
                  <div className="space-y-2">
                    <select
                      value={editingTemplate.sendDay}
                      onChange={(e) => setEditingTemplate(prev => ({ ...prev, sendDay: parseInt(e.target.value) }))}
                      className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-xs"
                    >
                      {SEND_DAY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {isRTL ? opt.labelAr : opt.labelEn}
                        </option>
                      ))}
                    </select>
                    
                    {selectedChannel === 'email' && (
                      <input
                        type="text"
                        value={editingTemplate.subject}
                        onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder={isRTL ? 'الموضوع...' : 'Subject...'}
                        className="w-full px-2 py-1.5 bg-slate-700/50 border border-slate-600 rounded text-white text-xs"
                      />
                    )}
                    
                    <textarea
                      value={editingTemplate.body}
                      onChange={(e) => setEditingTemplate(prev => ({ ...prev, body: e.target.value }))}
                      placeholder={isRTL ? 'محتوى الرسالة...\n\nمثال:\nمرحباً {name},\n\nنقاط مهمة:\n   - النقطة الأولى\n   - النقطة الثانية' : 'Message...\n\nExample:\nHello {name},\n\nImportant points:\n   - First point\n   - Second point'}
                      rows={6}
                      className="w-full px-2 py-1.5 bg-slate-700/50 border border-slate-600 rounded text-white text-sm font-mono resize-y whitespace-pre-wrap"
                      style={{ tabSize: 4 }}
                    />
                    
                    <button
                      onClick={() => saveTemplate(
                        editingTemplate.adNumber,
                        editingTemplate.subject,
                        editingTemplate.body,
                        editingTemplate.sendDay
                      )}
                      disabled={loading}
                      className="w-full py-1.5 bg-emerald-500 text-white rounded text-xs font-medium disabled:opacity-50"
                    >
                      {loading ? '...' : (isRTL ? 'حفظ' : 'Save')}
                    </button>
                  </div>
                ) : template ? (
                  <div className="space-y-0.5">
                    {selectedChannel === 'email' && template.subject && (
                      <p className="text-xs text-slate-400 truncate">{template.subject}</p>
                    )}
                    <p className="text-xs text-slate-300 line-clamp-2">{template.body || '-'}</p>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">{isRTL ? 'انقر للاعداد' : 'Click to setup'}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Info */}
      <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700 text-xs text-slate-400">
        <strong className="text-white">{isRTL ? 'اكتشاف اللغة:' : 'Language Detection:'}</strong>
        <span className="ms-1">Sprache (des Formulars): German = DE</span>
      </div>
    </div>
  );
}
